/**
 * « SKILLCARDS de mes enfants » (`/parent/skillcards`, migration 0326) : le
 * parent choisit un enfant lié, voit le programme de son année et tous ses
 * packs AVEC LE PRIX DE L'ENFANT (cartes qu'il a déjà déduites), et achète
 * directement pour lui — sans que l'enfant ait à le demander. L'enfant est
 * prévenu. Web seulement (App Store 3.1.1). Retour Stripe : `?purchase=`.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, CreditCard, Gift } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { cardPalette, offerContentLabel } from '@jeraisonne/db/cards';
import { levelLabel, packPeriodLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { AvatarCircle } from '@/components/avatar-circle';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import { useChildrenAccess } from '@/lib/parent/subscriptions';
import { PARENT_PLAN_PRICE } from '@/lib/parent/plan-seats';
import { useChildSkillcardOffers } from '@/lib/skillcards/gifts';
import {
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  useSkillcardPurchaseStatus,
} from '@/lib/skillcards/purchase';
import type { SkillcardOfferKind } from '@jeraisonne/db/cards';
import { SkillcardOfferRow } from './skillcard-offer-row';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';

export function ParentSkillcardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { child?: string; purchase?: string };
  const children = useChildrenAccess(user?.id ?? '');
  const list = children.data ?? [];
  const [picked, setPicked] = useState<string | null>(search.child ?? null);
  const childId = picked ?? list[0]?.child_id ?? null;
  const offers = useChildSkillcardOffers(childId);
  const purchase = useSkillcardPurchaseStatus(search.purchase ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = offers.data;
  const firstName = data?.student.first_name ?? 'votre enfant';

  async function buy(kind: SkillcardOfferKind, targetId: string, cardId: string | null) {
    if (!childId || !cardId) return;
    setError(null);
    setBusy(`${kind}:${targetId}`);
    try {
      window.location.href = await startSkillcardCheckout({
        kind,
        targetId,
        cardId,
        forStudentId: childId,
      });
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-5 px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to="/parent/children"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Mes enfants
      </Link>
      <div className="space-y-1">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">
          SKILLCARDS pour mes enfants
        </h1>
        <p className="text-ink-600 text-sm">
          Achetez directement les SKILLCARDS de son année : elles arrivent dans son deck et il en
          est prévenu. Les cartes qu’il possède déjà sont déduites.
        </p>
      </div>
      <SkillcardPromoBanner />

      {search.purchase && (
        <p
          role="status"
          className={cn(
            'flex items-center gap-2 rounded-2xl p-4 text-sm',
            purchase.data === 'paid'
              ? 'bg-green-50 text-green-800'
              : 'bg-primary-50 text-primary-800',
          )}
        >
          {purchase.data === 'paid' ? (
            <>
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden /> Paiement confirmé : les
              SKILLCARDS sont dans le deck de {firstName}.
            </>
          ) : purchase.data === 'failed' ? (
            'Le paiement n’a pas abouti.'
          ) : (
            'Paiement reçu, activation des SKILLCARDS…'
          )}
        </p>
      )}

      {isNativeApp() ? (
        <p className="text-ink-600 text-sm">
          L’achat de SKILLCARDS se fait sur le site jeraisonne.fr.
        </p>
      ) : children.isLoading ? (
        <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
      ) : list.length === 0 ? (
        <p className="border-ink-100 text-ink-500 rounded-2xl border border-dashed p-6 text-center text-sm">
          Aucun enfant relié à votre compte. Ajoutez-le depuis « Mes enfants ».
        </p>
      ) : (
        <>
          {list.length > 1 && (
            <div role="tablist" aria-label="Enfant" className="flex flex-wrap gap-2">
              {list.map((c) => {
                const on = c.child_id === childId;
                return (
                  <button
                    key={c.child_id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => {
                      setPicked(c.child_id);
                      void navigate({ to: '/parent/skillcards', search: { child: c.child_id } });
                    }}
                    className={cn(
                      'flex min-h-[2.75rem] items-center gap-2 rounded-full border px-3 text-sm font-semibold active:scale-[0.98]',
                      on
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-ink-200 text-ink-700',
                    )}
                  >
                    <AvatarCircle name={c.display_name ?? '?'} src={c.avatar_url} size="xs" />
                    {(c.display_name ?? 'Enfant').split(' ')[0]}
                  </button>
                );
              })}
            </div>
          )}

          {offers.isLoading ? (
            <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
          ) : !data ? (
            <p className="text-ink-500 text-sm">Impossible de charger les offres.</p>
          ) : data.premium ? (
            <p className="rounded-2xl bg-green-50 p-4 text-sm text-green-800">
              {firstName} a déjà un abonnement ou un pass : toutes les SKILLCARDS sont incluses.
            </p>
          ) : !data.student.level ? (
            <p className="text-ink-500 text-sm">
              Le niveau de {firstName} n’est pas encore renseigné.
            </p>
          ) : data.offers.length === 0 ? (
            <p className="text-ink-500 text-sm">
              Pas encore de SKILLCARDS pour le programme de {firstName}.
            </p>
          ) : (
            data.offers.map((o) => {
              const palette = cardPalette([data.student.level!]);
              const theme = { accent: palette.accent, background: palette.bg };
              const buyLabel = `Pour ${firstName}`;
              return (
                <section key={o.programme_id} className="space-y-2">
                  <h2 className="font-display text-ink-900 text-lg font-bold">
                    {subjectLabel(o.subject)} · {levelLabel(data.student.level!)}
                  </h2>
                  {o.programme && o.programme_card_id && (
                    <SkillcardOfferRow
                      quote={o.programme}
                      title="Tout le programme de l’année"
                      subtitle={offerContentLabel(o.programme)}
                      highlight="Le plus avantageux"
                      buyLabel={buyLabel}
                      {...theme}
                      busy={busy === `programme:${o.programme.target_id}`}
                      disabled={!!busy}
                      onBuy={() =>
                        void buy('programme', o.programme!.target_id, o.programme_card_id)
                      }
                    />
                  )}
                  {/* trop d'offres d'un coup : les packs sont repliés sous un seul titre */}
                  {o.packs.length > 0 && (
                    <details
                      className="border-ink-100 ocean:bg-white/5 group rounded-2xl border bg-white"
                      data-testid="pack-offers"
                    >
                      <summary className="flex min-h-[2.75rem] cursor-pointer list-none items-center gap-2 px-4 py-3">
                        <span className="font-display text-ink-900 flex-1 text-base font-bold">
                          Offre par pack
                        </span>
                        <span className="text-ink-500 text-sm">
                          {o.packs.length} pack{o.packs.length > 1 ? 's' : ''}
                        </span>
                        <ChevronDown
                          className="text-ink-500 h-5 w-5 transition-transform group-open:rotate-180"
                          aria-hidden
                        />
                      </summary>
                      <div className="space-y-2 px-2 pb-2">
                        {o.packs.map((p) => (
                          <SkillcardOfferRow
                            key={p.target_id}
                            quote={p}
                            title={p.pack_title}
                            subtitle={`${packPeriodLabel(p.start_month, p.end_month)} · ${offerContentLabel(p)}`}
                            buyLabel={buyLabel}
                            {...theme}
                            busy={busy === `pack:${p.target_id}`}
                            disabled={!!busy || !p.card_id}
                            onBuy={() => void buy('pack', p.target_id, p.card_id)}
                          />
                        ))}
                      </div>
                    </details>
                  )}
                </section>
              );
            })
          )}
          {error && (
            <p role="alert" className="text-error-600 text-sm">
              {error}
            </p>
          )}
          {data && !data.premium && (
            // l'autre façon : un abonnement, qui inclut TOUTES les SKILLCARDS (et le reste)
            <Link
              to="/parent/subscriptions"
              data-testid="subscription-offer"
              className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border p-4 active:scale-[0.98]"
            >
              <CreditCard className="text-primary-500 h-6 w-6 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="font-display text-ink-900 block text-base font-bold">
                  Ou l’abonnement : tout est inclus
                </span>
                <span className="text-ink-600 block text-sm">
                  Toutes les SKILLCARDS, tous les exercices. Élève{' '}
                  {PARENT_PLAN_PRICE.student_monthly}/mois, Famille{' '}
                  {PARENT_PLAN_PRICE.family_monthly}/mois jusqu’à 3 enfants. Résiliable à tout
                  moment.
                </span>
              </span>
              <ChevronRight className="text-ink-400 h-5 w-5 shrink-0" aria-hidden />
            </Link>
          )}
          <Link
            to="/cadeaux"
            className="border-ink-200 text-ink-800 ocean:bg-white/5 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border bg-white p-4 text-sm active:scale-[0.98]"
          >
            <Gift className="text-accent-500 h-5 w-5 shrink-0" aria-hidden />
            <span>
              Pour offrir des SKILLCARDS à quelqu’un d’autre (code cadeau à partager), ouvrez une
              carte puis « Offrir à quelqu’un ». Vos codes sont dans « Mes cadeaux ».
            </span>
          </Link>
        </>
      )}
    </div>
  );
}
